import { useCallback, useEffect, useRef } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { LocalStorageConfigs } from "../../../utils/LocalStorage";
import { useObx } from "../../../utils/UseObx";
import ApiRequestPaths from "../../Constants/ApiRequestPaths";

export interface IUseUpsertCustomerNoteProps {
  /** The id of the lead for the current application session. */
  leadId: string;
  /** The id of the customer for whom the note is being created or updated. */
  customerId: string;
  /** The id of the center the note is associated with. */
  centerId: string;
}

interface INewCustomerNoteRequest {
  /** The content of the note. */
  text: string;
  /** The ID of the center the note is associated with. */
  centerId: string;
}

interface ICustomerNote extends INewCustomerNoteRequest {
  /** The primary identifier of the note to the application. */
  id: string;
}

/** A triggered hook for PUTing or POSTing a customer note.
 * @remarks This hook will cache the note id to local storage when a note is created or updated.
 */
const useUpsertCustomerNote: TriggeredHook<ICustomerNote, string, boolean, IUseUpsertCustomerNoteProps> = ({
  leadId,
  centerId,
  customerId
}) => {
  const noteIdRef = useRef(localStorage.getItem(LocalStorageConfigs.customerNoteId.key));
  const { setReq, res, err, loading } = useObx<ICustomerNote, INewCustomerNoteRequest & Partial<ICustomerNote>>(
    "UpsertCustomerNote"
  );

  const execute = useCallback((noteContent: string) => {
    const payload = { text: noteContent, centerId };
    setReq(
      noteIdRef.current
        ? {
            url: ApiRequestPaths.putCustomerNote(leadId, customerId, noteIdRef.current),
            method: "put",
            payload: { ...payload, id: noteIdRef.current }
          }
        : {
            url: ApiRequestPaths.postCustomerNote(leadId, customerId),
            method: "post",
            payload
          }
    );
  }, []);

  useEffect(() => {
    if (res?.payload) {
      noteIdRef.current = res.payload.id;
      localStorage.setItem(LocalStorageConfigs.customerNoteId.key, res.payload.id);
    }
  }, [res]);

  return { execute, result: res?.payload || null, error: Boolean(err), isLoading: loading };
};

export default useUpsertCustomerNote;
